import type { NextPage } from 'next';
import { useRouter } from 'next/router';

import {
  BadgeCheckIcon,
  ClipboardCheckIcon,
  SupportIcon
} from '@heroicons/react/outline';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  CoverageSection,
  FadeIn,
  FaqSection,
  Footer,
  GradientBg,
  Header,
  InsuranceSection,
  PageHeader,
  WhoNeedsInsuranceSection,
  WhyIsInsuranceImportant
} from '../components';
import { WorkersCompensationSvg } from '../components/svg';

const coverageItems = [
  {
    title: 'Medical Expenses',
    description:
      'If an employee is injured or gets sick on the job, a worker’s compensation policy can cover employee medical expenses and lost wages.',
    icon: BadgeCheckIcon
  },
  {
    title: 'Employee Liability',
    description:
      'If an employee files a lawsuit due to an injury or illness sustained at the workplace, regardless of if the claim is valid, a workers’ compensation policy can cover legal expenses associated with claim.',
    icon: ClipboardCheckIcon
  },
  {
    title: 'Survivor Benefits',
    description:
      'In the event of an employee fatality, the beneficiaries entitled to cash benefits would be covered under a workers’ compensation policy.',
    icon: SupportIcon
  }
];
const faqs = [
  {
    question: "Is workers' comp. required by law?",
    answer: `Workers' compensation is regulated on the state level, and each state has its own requirements and penalties. Nearly every state requires employers to carry workers' compensation insurance.`
  },
  {
    question: "How do I show proof of a workers' comp. policy?",
    answer: `Once you've purchased your workers' comp policy, a Certificate of Insurance (“COI”) will be sent to you and can be presented to any party that requires proof of coverage. You can also request that the COI be sent to any third-party that requests verification of insurance.`
  },
  {
    question: 'Do I need workers comp. insurance if I am self-employed?',
    answer: `You may not by legally required to have a workers' comp policy if you are self-employed. However, you may be required to show proof of a workers’ comp policy for certain business contracts. Additionally, a workers’ comp policy enables you as a self-employed worker to mitigate financial risks relating to potential lost wages and medical expenses.`
  },
  {
    question:
      "If I am the owner of a Company am I covered under a workers' comp policy?",
    answer: `Inclusion of coverage for Company owners and officers varies by policy. Certain states require coverage for owners and officers. Consult with your Coverdash agent to get owners and officers covered under your policy.`
  }
];

const WorkersCompensation: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="Workers' compensation"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: `Workers' compensation`
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Is workers' comp. required by law?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Workers' compensation is regulated on the state level, and each state has its own requirements and penalties. Nearly every state requires employers to carry workers' compensation insurance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I show proof of a workers' comp. policy?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Once you've purchased your workers' comp policy, a Certificate of Insurance (COI) will be sent to you and can be presented to any party that requires proof of coverage. You can also request that the COI be sent to any third-party that requests verification of insurance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do I need workers comp. insurance if I am self-employed?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "You may not be legally required to have a workers' comp policy if you are self-employed. However, you may be required to show proof of a workers' comp policy for certain business contracts. Additionally, a workers' comp policy enables you as a self-employed worker to mitigate financial risks relating to potential lost wages and medical expenses."
                  }
                },
                {
                  "@type": "Question",
                  "name": "If I am the owner of a Company am I covered under a workers' comp policy?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Inclusion of coverage for Company owners and officers varies by policy. Certain states require coverage for owners and officers. Consult with your Coverdash agent to get owners and officers covered under your policy."
                  }
                }
              ]
            })
          }}
        />
      </Head>
      <GradientBg />
      <Header />
      <PageHeader
        title="Workers compensation"
        image={
          <>
            <WorkersCompensationSvg className="hidden sm:block mx-auto max-w-4xl w-full mt-8 md:mt-0" />
            <img
              src="/img/sb4.jpg"
              className="sm:hidden w-full max-w-4xl shadow rounded overflow-hidden"
            />
          </>
        }
      />

      {/* Section-1 */}
      <div className="relative">
        <div className="max-w-7xl mx-auto pb-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
          <FadeIn>
            <h2 className="max-w-5xl mx-auto text-5xl text-alt font-extrabold my-7">
              What is <span className="text-primary">workers compensation</span>{' '}
              insurance?
            </h2>
          </FadeIn>
          <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
            Workers' compensation insurance, also known as workers’ comp
            insurance, covers costs of medical care and lost wages to employees
            that suffer any injury or illness as a direct result of employment.
            In almost all U.S. states, a workers’ comp policy is required by law
            to protect employees and cover their wages and medical bills in the
            event of a workplace related injury.
          </p>
        </div>
      </div>

      {/* Section-2 */}
      <CoverageSection title="workers compensation" items={coverageItems} />

      {/* Section-3 */}
      <WhoNeedsInsuranceSection
        title="workers compensation"
        description={`In nearly all states, any business with employees will need to have a workers' comp policy. Even self-employed professionals and freelancers may be required to have a workers' comp policy to meet certain state or contractual requirements.`}
      />

      {/* Section-4 */}
      <WhyIsInsuranceImportant>
        <FadeIn>
          <h2 className="text-5xl text-alt font-extrabold my-7">
            Why is a <span className="text-primary">workers compensation</span>{' '}
            policy important?
          </h2>
        </FadeIn>

        <p className="mt-4 text-lg text-gray-500">
          In almost all U.S. state, if you employ any individuals as part of
          your business, a workers' compensation policy is required by law. If
          an employee is injured or falls ill as a direct result of employment,
          the business is liable for covering employee medical expenses and lost
          wages which can cause severe financial harm. Having a workers'
          compensation policy protects your employees and your business from
          unexpected harm and injury.
        </p>
      </WhyIsInsuranceImportant>

      {/* Section-5 */}
      <InsuranceSection />

      {/* Section-6 */}
      <FaqSection title="Workers compensation insurance" faqs={faqs} />

      <BottomGraphic />
      <Footer />
    </>
  );
};

export default WorkersCompensation;
